import { Row, Col, Card, Typography, DatePicker, Button, Select } from 'antd';
import { useState } from 'react';
import {
  downloadDailyPnL,
  downloadDailyPnLByStrategy,
  downloadDailyGreeks,
  downloadDailyGreeksPnL,
  downloadDailyModelPerformance,
  downloadIndexPrice,
} from 'src/services/download.service';
import { useQuery } from '@tanstack/react-query';
import { getAccounts } from 'src/services/account.service';
import { getSpecs } from 'src/services/spec.service';

const { Title } = Typography;

function Download() {
  const [timeRange, setTimeRange] = useState();
  const [currentAccount, setCurrentAccount] = useState();
  const [currentSpec, setCurrentSpec] = useState();
  const [currentCurrency, setCurrentCurrency] = useState('btc');

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: getAccounts,
  });

  const { data: specsData, isLoading: specsLoading } = useQuery({
    queryKey: ['specs'],
    queryFn: getSpecs,
  });

  const exportCsv = (data, filename) => {
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      timeRange
        ? `${filename}_${timeRange[0].format('YYYYMMDD')}_${timeRange[1].format(
            'YYYYMMDD'
          )}.csv`
        : `${filename}_all.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadDailyPnL = () => {
    downloadDailyPnL({
      queryKey: [
        'dailyPnL',
        {
          start_date: timeRange && timeRange[0].format('YYYY-MM-DD'),
          end_date: timeRange && timeRange[1].format('YYYY-MM-DD'),
        },
      ],
    })
      .then((csvData) => {
        exportCsv(csvData, 'daily_pnl');
      })
      .catch((error) => {
        console.error('Error downloading data:', error);
      });
  };

  const handleDownloadDailyPnLByStrategy = () => {
    downloadDailyPnLByStrategy({
      queryKey: [
        'dailyPnLByStrategy',
        {
          start_date: timeRange && timeRange[0].format('YYYY-MM-DD'),
          end_date: timeRange && timeRange[1].format('YYYY-MM-DD'),
          account_name: currentAccount || accountsData?.result[0]?.account_name,
        },
      ],
    })
      .then((csvData) => {
        exportCsv(csvData, 'daily_pnl_by_strategy');
      })
      .catch((error) => {
        console.error('Error downloading data:', error);
      });
  };

  const handleDownloadDailyGreeks = () => {
    downloadDailyGreeks({
      queryKey: [
        'dailyGreeks',
        {
          start_date: timeRange && timeRange[0].format('YYYY-MM-DD'),
          end_date: timeRange && timeRange[1].format('YYYY-MM-DD'),
          account_name: currentAccount || accountsData?.result[0]?.account_name,
        },
      ],
    })
      .then((csvData) => {
        exportCsv(csvData, 'daily_greeks');
      })
      .catch((error) => {
        console.error('Error downloading data:', error);
      });
  };

  const handleDownloadDailyGreeksPnL = () => {
    downloadDailyGreeksPnL({
      queryKey: [
        'dailyGreeksPnL',
        {
          start_date: timeRange && timeRange[0].format('YYYY-MM-DD'),
          end_date: timeRange && timeRange[1].format('YYYY-MM-DD'),
          account_name: currentAccount || accountsData?.result[0]?.account_name,
        },
      ],
    })
      .then((csvData) => {
        exportCsv(csvData, 'daily_greeks_pnl');
      })
      .catch((error) => {
        console.error('Error downloading data:', error);
      });
  };

  const handleDownloadDailyModelPerformance = () => {
    downloadDailyModelPerformance({
      queryKey: [
        'dailyModelPerformance',
        {
          start_date: timeRange && timeRange[0].format('YYYY-MM-DD'),
          end_date: timeRange && timeRange[1].format('YYYY-MM-DD'),
          spec: currentSpec || specsData?.result[0],
        },
      ],
    })
      .then((csvData) => {
        exportCsv(csvData, 'daily_model_performance');
      })
      .catch((error) => {
        console.error('Error downloading data:', error);
      });
  };

  const handleDownloadIndexPrice = () => {
    downloadIndexPrice({
      queryKey: [
        'indexPrice',
        {
          start_date: timeRange && timeRange[0].format('YYYY-MM-DD'),
          end_date: timeRange && timeRange[1].format('YYYY-MM-DD'),
          currency: currentCurrency || 'btc',
        },
      ],
    })
      .then((csvData) => {
        exportCsv(csvData, 'index_price');
      })
      .catch((error) => {
        console.error('Error downloading data:', error);
      });
  };

  return (
    <>
      <div className='layout-content'></div>
      <Row gutter={[16, 16]} className='mb-24'>
        <Col span={24}>
          <Card>
            <Title level={3}>Download Daily PnL</Title>
            {/* Add download options here */}
            <p>If you don't select date, all data will be downloaded.</p>
            <DatePicker.RangePicker
              value={timeRange}
              onChange={(dates) => setTimeRange(dates)}
              style={{ width: '100%', marginBottom: '16px' }}
              allowClear
            />
            {accountsLoading ? (
              <></>
            ) : (
              <Select
                style={{ width: '100%' }}
                placeholder='Select Account'
                loading={accountsLoading}
                options={accountsData?.result.map((account) => ({
                  label: account.account_name,
                  value: account.account_name,
                }))}
                defaultValue={accountsData?.result[0]?.account_name}
                onChange={(value) => setCurrentAccount(value)}
              />
            )}
            <Row>
              <Col>
                <Button
                  type='primary'
                  style={{ marginTop: '16px' }}
                  onClick={() => {
                    // Handle download logic here
                    console.log('Downloading data for:', timeRange);
                    handleDownloadDailyPnL();
                  }}
                >
                  Download Daily PnL
                </Button>
              </Col>
            </Row>
            <Row>
              <Col>
                <Button
                  type='primary'
                  style={{ marginTop: '16px' }}
                  onClick={() => {
                    handleDownloadDailyPnLByStrategy();
                  }}
                >
                  Download Daily PnL by Strategy
                </Button>
              </Col>
            </Row>

            <Row>
              <Col>
                <Button
                  type='primary'
                  style={{ marginTop: '16px' }}
                  onClick={() => {
                    handleDownloadDailyGreeks();
                  }}
                >
                  Download Daily Greeks
                </Button>
              </Col>
            </Row>
            <Row>
              <Col>
                <Button
                  type='primary'
                  style={{ marginTop: '16px' }}
                  onClick={() => {
                    handleDownloadDailyGreeksPnL();
                  }}
                >
                  Download Daily Greeks PnL
                </Button>
              </Col>
            </Row>

            {specsLoading ? (
              <></>
            ) : (
              <Select
                style={{
                  marginTop: '24px',
                  width: '100%',
                  marginBottom: '16px',
                }}
                placeholder='Select Spec'
                loading={specsLoading}
                options={specsData?.result.map((spec) => ({
                  label: spec,
                  value: spec,
                }))}
                defaultValue={specsData?.result[0]}
                onChange={(value) => setCurrentSpec(value)}
              />
            )}
            <Row>
              <Col>
                <Button
                  type='primary'
                  style={{}}
                  onClick={() => {
                    handleDownloadDailyModelPerformance();
                  }}
                >
                  Download Daily Model Performance
                </Button>
              </Col>
            </Row>

            <Select
              style={{
                marginTop: '24px',
                width: '100%',
                marginBottom: '16px',
              }}
              placeholder='Select Currency'
              value={currentCurrency}
              onChange={(value) => setCurrentCurrency(value)}
            >
              <Select.Option value='btc'>BTC</Select.Option>
            </Select>
            <Row>
              <Col>
                <Button
                  type='primary'
                  style={{}}
                  onClick={() => {
                    handleDownloadIndexPrice();
                  }}
                >
                  Download Index Price
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default Download;
